import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAccess, DEFAULT_PLAN } from "@/context/AccessContext";
import { PlanPicker } from "./PlanPicker";
import { ReferralCard } from "./ReferralCard";
import { ProfileStatusCard, fmtDate } from "./ProfileStatusCard";
import { BillingHistory, PassTimeline } from "./ProfileBilling";
import { toast } from "sonner";

function SubscribeBlock({ data, plans, plan, onPlan, onGo, checkingOut, trialDays }) {
  const selectedPrice = plans.find((p) => p.lookup_key === plan)?.price_usd ?? 4.99;
  return (
    <div className="mt-2" data-testid="profile-subscribe-block">
      <div className="label-caps text-parchment/60 mb-2 flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-oracle" strokeWidth={1.5} />
        {data.active ? `Switch to a subscription — ${trialDays}-day free trial, your pass days are kept` : `Choose a plan · ${trialDays}-day free trial`}
      </div>
      <PlanPicker plans={plans} value={plan} onChange={onPlan} compact />
      <Button data-testid="profile-extend-btn" onClick={onGo} disabled={checkingOut} className="mt-3 w-full bg-oracle text-ink hover:bg-[#FFC033] label-caps">
        {checkingOut ? <><RefreshCw className="w-3 h-3 mr-2 animate-spin" /> Opening checkout…</> : `Start free trial · then $${selectedPrice.toFixed(2)}`}
      </Button>
    </div>
  );
}

export function ProfileDialog({ open, onOpenChange, email }) {
  const { plans, checkingOut, subscribe, openPortal, cancelPaypal, trial_days } = useAccess();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(DEFAULT_PLAN);
  const [portalBusy, setPortalBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const loadData = useCallback(() => api.get("/me/passes").then((r) => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => {
    if (open) { setConfirmCancel(false); loadData(); }
  }, [open, loadData]);

  const go = async () => {
    try { await subscribe(plan); } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };
  const manage = async () => {
    setPortalBusy(true);
    try { await openPortal(); } catch (e) { toast.error(e.response?.data?.detail || "Could not open the billing portal"); setPortalBusy(false); }
  };
  const cancelPp = async () => {
    if (!confirmCancel) { setConfirmCancel(true); return; }
    setPortalBusy(true);
    try {
      const r = await cancelPaypal();
      toast.success(r.expires_at ? `Cancelled — you keep access until ${fmtDate(r.expires_at)}` : "Subscription cancelled");
      setConfirmCancel(false);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setPortalBusy(false);
    }
  };

  const labels = data?.plan_labels || {};
  const sub = data?.subscription;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-dialog" className="bg-surface border-white/10 text-parchment max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Your <span className="italic text-oracle">subscription</span></DialogTitle>
          <p className="font-body text-xs text-parchment/50">{email}</p>
        </DialogHeader>

        {!data ? (
          <div className="py-10 text-center label-caps text-parchment/40">Loading…</div>
        ) : (
          <>
            <ProfileStatusCard data={data} sub={sub} labels={labels} portalBusy={portalBusy} confirmCancel={confirmCancel} onManage={manage} onCancelPaypal={cancelPp} />
            {!sub && <SubscribeBlock data={data} plans={plans} plan={plan} onPlan={setPlan} onGo={go} checkingOut={checkingOut} trialDays={trial_days} />}
            {data.active && <div className="mt-4"><ReferralCard compact /></div>}
            <BillingHistory purchases={data.purchases} labels={labels} />
            <PassTimeline passes={data.passes} labels={labels} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
